import React, { FC, memo, ReactNode } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker as AntdDatePicker } from 'antd';
import { DatePickerProps as AntdDatePickerProps } from 'antd/es/date-picker';
import s from './index.module.scss';
import classNames from 'classnames';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import { CLIENT_DATE_FORMAT } from 'shared/utils/constants';
import { DatePickerInput } from '../DatePickerInput';

interface ModernDatePickerProps {
  format?: string;
  className?: string;
  onChange?: (date: Dayjs) => void;
  placeholder?: string;
  disabled?: boolean;
  inputReadOnly?: boolean;
  disabledDate?: (date: Dayjs) => boolean;
  showToday?: boolean;
  showPresets?: boolean;
  value?: Nullable<Dayjs>;
  defaultPickerValue?: Nullable<Dayjs>;
  bordered?: boolean;
  requiredMark?: boolean;
  presets?: AntdDatePickerProps['presets'];
  extraFooter?: Nullable<ReactNode>;
  size?: SizeType;
}

export const ModernDatePicker: FC<ModernDatePickerProps> = memo((props) => {
  const {
    format,
    className,
    onChange,
    placeholder,
    disabled,
    inputReadOnly,
    disabledDate,
    showToday,
    showPresets,
    value,
    defaultPickerValue,
    requiredMark,
    bordered = false,
    presets,
    extraFooter,
    size = 'middle',
  } = props;

  const { t } = useAppTranslation('common');

  const defaultPresets = [
    {
      label: t('Today'),
      value: dayjs(),
    },
    {
      label: t('7 Days ago'),
      value: dayjs().subtract(7, 'day'),
    },
    {
      label: t('2 Weeks ago'),
      value: dayjs().subtract(2, 'week'),
    },
    {
      label: t('1 Month ago'),
      value: dayjs().subtract(1, 'month'),
    },
    {
      label: t('3 Months ago'),
      value: dayjs().subtract(3, 'month'),
    },
    {
      label: t('6 Months ago'),
      value: dayjs().subtract(6, 'month'),
    },
    {
      label: t('1 Year ago'),
      value: dayjs().subtract(1, 'year'),
    },
    {
      label: t('Start of previous month'),
      value: dayjs().subtract(1, 'month').startOf('month'),
    },
    {
      label: t('End of previous month'),
      value: dayjs().subtract(1, 'month').endOf('month'),
    },
  ];

  const isDatePickerInputNeeded = format === CLIENT_DATE_FORMAT || !inputReadOnly;

  return (
    <div className="relative">
      {requiredMark && <div className="text-error text-2xl absolute z-10 top-0 left-1 pointer-events-none">*</div>}
      <AntdDatePicker
        format={format}
        className={classNames(s.datepicker, s[size], { [s.borderless]: !bordered }, className)}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        inputReadOnly={inputReadOnly}
        disabledDate={disabledDate}
        value={value || undefined}
        defaultPickerValue={defaultPickerValue}
        presets={showPresets ? presets || defaultPresets : undefined}
        showNow={!showPresets && showToday}
        renderExtraFooter={() => extraFooter}
        components={{
          input: isDatePickerInputNeeded ? DatePickerInput : undefined,
        }}
      />
    </div>
  );
});
